import type { CoordinatorLabTourSlug } from './coordinatorLearningLabTours.js';
import type { LearningLabPlaybook } from './missionTypes.js';
import {
  EVENT_CREATE_WIZARD_ALLOCATION_INDEX,
  EVENT_CREATE_WIZARD_LAST_INDEX,
  EVENT_CREATE_WIZARD_STEPS,
} from '../events/eventCreateWizard.js';

const cancelPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_cancel_event',
  title: 'Cancel an event',
  groups: [
    { id: 'start', title: 'Get started' },
    { id: 'cancel', title: 'Cancel flow' },
    { id: 'finish', title: 'Wrap up' },
  ],
  steps: [
    {
      id: 'cancel_intro',
      stepName: 'lab_cancel_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Practice cancelling an event',
      instruction:
        'You will walk through cancelling a sample event. No real data is changed — confirmations show a practice message only.',
      why: 'Coordinators sometimes need to cancel training when schedules change or enrollment is too low.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'cancel_events_tab',
      stepName: 'lab_cancel_events_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-events',
      title: 'Open Agency Events',
      instruction: 'Click the Events tab to see published and draft events for the sample agency.',
      why: 'Every cancellation starts from the events list for your agency.',
      module: 'coordinator',
      coordinatorTabSlug: 'events',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-events' },
    },
    {
      id: 'cancel_row',
      stepName: 'lab_cancel_row',
      kind: 'spotlight',
      groupId: 'cancel',
      tourAnchor: 'coordinator-event-row-event_leadership_cancel',
      title: 'Select the sample event',
      instruction:
        'Find the Leadership Development Seminar row — it is seeded for practice in the Learning Lab.',
      why: 'You cancel one event at a time from its row actions.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'events',
      beforeAction: 'switchCoordinatorTab',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'cancel_button',
      stepName: 'lab_cancel_button',
      kind: 'interact',
      groupId: 'cancel',
      tourAnchor: 'coordinator-event-cancel-btn',
      title: 'Open Cancel',
      instruction: 'Click Cancel on the event row to open the cancellation dialog.',
      tip: 'In production, only coordinators with access to the event see this action.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'events',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'open-modal',
      metadata: { rowAnchor: 'coordinator-event-row-event_leadership_cancel' },
      advanceGate: { type: 'click', anchor: 'coordinator-event-cancel-btn' },
    },
    {
      id: 'cancel_modal',
      stepName: 'lab_cancel_modal',
      kind: 'form',
      groupId: 'cancel',
      tourAnchor: 'coordinator-event-cancel-modal',
      title: 'Review the cancellation dialog',
      instruction:
        'The dialog explains refunds, notifications, and audit logging. Sample stats are shown for practice.',
      why: 'Cancelling affects attendees — the dialog makes consequences clear before you confirm.',
      isPanelTarget: true,
      advanceGate: {
        type: 'dom',
        event: 'apt-tour:modal-opened',
        detail: { modalId: 'event-cancel' },
      },
    },
    {
      id: 'cancel_reason',
      stepName: 'lab_cancel_reason',
      kind: 'form',
      groupId: 'cancel',
      tourAnchor: 'coordinator-event-cancel-reason',
      title: 'Enter a reason',
      instruction: 'Type a short reason in the reason field (for example: "Low enrollment").',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'events',
      metadata: { requiredModal: 'event-cancel' },
      advanceGate: { type: 'manual' },
    },
    {
      id: 'cancel_confirm',
      stepName: 'lab_cancel_confirm',
      kind: 'confirm',
      groupId: 'finish',
      tourAnchor: 'coordinator-event-cancel-submit-btn',
      title: 'Confirm cancellation (practice)',
      instruction:
        'Click Cancel Event to finish. In the Lab, the request is intercepted — you will see a practice toast instead of a live cancel.',
      tip: 'On your real agency, this would notify registrants and process refunds when applicable.',
      isPanelTarget: true,
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      metadata: { requiredModal: 'event-cancel' },
      advanceGate: { type: 'click', anchor: 'coordinator-event-cancel-submit-btn' },
    },
    {
      id: 'cancel_complete',
      stepName: 'lab_cancel_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Cancel flow complete',
      instruction: 'You have practiced the cancellation workflow. No live events were changed.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};

const createEventPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_create_event',
  title: 'Create an event',
  groups: [
    { id: 'start', title: 'Get started' },
    { id: 'navigate', title: 'Navigate' },
    { id: 'build', title: 'Build event' },
    { id: 'finish', title: 'Finish' },
  ],
  steps: [
    {
      id: 'create_intro',
      stepName: 'lab_create_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Create a training event',
      instruction:
        'You will navigate to event create, then walk through the six-step builder on a seeded draft.',
      why: 'Creating events is the core coordinator workflow for publishing agency training.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'create_events_tab',
      stepName: 'lab_create_events_tab',
      kind: 'navigate',
      groupId: 'navigate',
      tourAnchor: 'coordinator-tab-events',
      title: 'Open Events',
      instruction: 'From the coordinator dashboard, open the Events tab.',
      module: 'coordinator',
      coordinatorTabSlug: 'events',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-events' },
    },
    {
      id: 'create_btn',
      stepName: 'lab_create_btn',
      kind: 'interact',
      groupId: 'navigate',
      tourAnchor: 'coordinator-create-event-btn',
      title: 'Click Create Event',
      instruction: 'Click Create Event to open the event builder.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'events',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'navigate',
      advanceGate: { type: 'click', anchor: 'coordinator-create-event-btn' },
    },
    {
      id: 'create_draft_load',
      stepName: 'lab_create_draft_load',
      kind: 'navigate',
      groupId: 'build',
      tourAnchor: 'event-form-stepper',
      title: 'Open the practice draft',
      instruction:
        'The Lab loads a seeded CPR draft so you can explore a realistic form. Continue when the stepper appears.',
      metadata: { enableDraftRedirect: true },
      advanceGate: { type: 'route', pathPattern: '/coordinator-event' },
      isPanelTarget: true,
    },
    {
      id: 'create_stepper',
      stepName: 'lab_create_stepper',
      kind: 'spotlight',
      groupId: 'build',
      tourAnchor: 'event-form-stepper',
      title: 'Six-step builder',
      instruction: `Events move through ${EVENT_CREATE_WIZARD_STEPS.join(' → ')}.`,
      isPanelTarget: true,
      advanceGate: { type: 'manual' },
    },
    {
      id: 'create_description',
      stepName: 'lab_create_description',
      kind: 'form',
      groupId: 'build',
      tourAnchor: 'event-form-description',
      title: 'Event details',
      instruction: 'Review title and description on the Basics step.',
      why: 'Accurate details help officers find and register for the right training.',
      isPanelTarget: true,
      metadata: { stepperIndex: 0 },
      targetZone: 'form',
      scrollPriority: 'safe-viewport',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'create_allocation',
      stepName: 'lab_create_allocation',
      kind: 'form',
      groupId: 'build',
      tourAnchor: 'event-form-allocation',
      title: 'Allocation settings',
      instruction: 'Allocation controls seat allocation across agencies, counties, or open registration.',
      isPanelTarget: true,
      metadata: { stepperIndex: EVENT_CREATE_WIZARD_ALLOCATION_INDEX },
      advanceGate: { type: 'stepper', stepIndex: EVENT_CREATE_WIZARD_ALLOCATION_INDEX },
    },
    {
      id: 'create_review',
      stepName: 'lab_create_review',
      kind: 'form',
      groupId: 'build',
      tourAnchor: 'event-form-review',
      title: 'Review & publish',
      instruction: 'Review summarizes the event before publish. Check the checklist.',
      isPanelTarget: true,
      metadata: { stepperIndex: EVENT_CREATE_WIZARD_LAST_INDEX },
      advanceGate: { type: 'stepper', stepIndex: EVENT_CREATE_WIZARD_LAST_INDEX },
    },
    {
      id: 'create_publish',
      stepName: 'lab_create_publish',
      kind: 'confirm',
      groupId: 'finish',
      tourAnchor: 'event-form-publish',
      title: 'Publish (practice)',
      instruction:
        'Click Publish to see what happens — the Lab blocks the save and shows a practice toast.',
      tip: 'On your real agency, publishing makes the event visible for registration.',
      isPanelTarget: true,
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      metadata: { stepperIndex: EVENT_CREATE_WIZARD_LAST_INDEX },
      advanceGate: { type: 'click', anchor: 'event-form-publish' },
    },
  ],
};

const createUserPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_create_user',
  title: 'Add a member',
  groups: [
    { id: 'start', title: 'Members' },
    { id: 'form', title: 'Create user' },
    { id: 'finish', title: 'Wrap up' },
  ],
  steps: [
    {
      id: 'user_intro',
      stepName: 'lab_user_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Add an agency member',
      instruction: 'Practice inviting a new member to your agency roster.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'user_members_tab',
      stepName: 'lab_user_members_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-members',
      title: 'Open Members',
      instruction: 'Open the Members tab on the coordinator dashboard.',
      module: 'coordinator',
      coordinatorTabSlug: 'members',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-members' },
    },
    {
      id: 'user_create_btn',
      stepName: 'lab_user_create_btn',
      kind: 'interact',
      groupId: 'form',
      tourAnchor: 'coordinator-create-user-btn',
      title: 'Add member',
      instruction: 'Click Add Member to open the create-user dialog.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'members',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'open-modal',
      advanceGate: { type: 'click', anchor: 'coordinator-create-user-btn' },
    },
    {
      id: 'user_modal',
      stepName: 'lab_user_modal',
      kind: 'form',
      groupId: 'form',
      tourAnchor: 'coordinator-create-user-form',
      title: 'Fill in member details',
      instruction: 'Enter name and email in the form. Required fields are marked.',
      isPanelTarget: true,
      advanceGate: {
        type: 'dom',
        event: 'apt-tour:modal-opened',
        detail: { modalId: 'create-user' },
      },
    },
    {
      id: 'user_submit',
      stepName: 'lab_user_submit',
      kind: 'confirm',
      groupId: 'form',
      tourAnchor: 'coordinator-create-user-submit-btn',
      title: 'Submit (practice)',
      instruction:
        'Click Create User — the Lab intercepts the request and shows a practice message.',
      isPanelTarget: true,
      metadata: { requiredModal: 'create-user' },
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-create-user-submit-btn' },
    },
    {
      id: 'user_complete',
      stepName: 'lab_user_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Member flow complete',
      instruction: 'You have practiced adding a member. Close the dialog when ready.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};

const venuesPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_manage_venues',
  title: 'Manage venues',
  groups: [
    { id: 'start', title: 'Venues' },
    { id: 'finish', title: 'Wrap up' },
  ],
  steps: [
    {
      id: 'venues_intro',
      stepName: 'lab_venues_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Agency venues',
      instruction: 'Venues are locations where your agency hosts training.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'venues_tab',
      stepName: 'lab_venues_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-venues',
      title: 'Open Venues',
      instruction: 'Open the Venues tab.',
      module: 'coordinator',
      coordinatorTabSlug: 'venues',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-venues' },
    },
    {
      id: 'venues_list',
      stepName: 'lab_venues_list',
      kind: 'spotlight',
      groupId: 'start',
      tourAnchor: 'coordinator-venue-list',
      title: 'Venue list',
      instruction: 'Sample venues are pre-loaded in the Learning Lab for you to explore.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'venues',
      beforeAction: 'switchCoordinatorTab',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'venues_create',
      stepName: 'lab_venues_create',
      kind: 'interact',
      groupId: 'start',
      tourAnchor: 'coordinator-venue-create-btn',
      title: 'Add a venue (practice)',
      instruction:
        'Click Add Venue if visible — saves are blocked in the Lab with a practice toast.',
      isPanelTarget: true,
      optional: true,
      module: 'coordinator',
      coordinatorTabSlug: 'venues',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-venue-create-btn' },
    },
    {
      id: 'venues_complete',
      stepName: 'lab_venues_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Venues overview complete',
      instruction: 'You know where to manage venues when scheduling events.',
      module: 'coordinator',
      coordinatorTabSlug: 'venues',
      beforeAction: 'switchCoordinatorTab',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};

const embedPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_embed_calendar',
  title: 'Embed calendar',
  groups: [
    { id: 'start', title: 'Embed' },
    { id: 'tabs', title: 'Configure' },
    { id: 'finish', title: 'Publish code' },
  ],
  steps: [
    {
      id: 'embed_intro',
      stepName: 'lab_embed_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Share your calendar',
      instruction: 'Embed a public calendar widget on your agency website.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'embed_tab',
      stepName: 'lab_embed_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-embed',
      title: 'Open Embed',
      instruction: 'Open the Embed Calendar tab.',
      module: 'coordinator',
      coordinatorTabSlug: 'embed',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-embed' },
    },
    {
      id: 'embed_settings',
      stepName: 'lab_embed_settings',
      kind: 'spotlight',
      groupId: 'tabs',
      tourAnchor: 'embed-tab-settings',
      title: 'Settings',
      instruction: 'Configure colors, layout, and default filters.',
      metadata: { embedTab: 'settings' },
      beforeAction: 'setEmbedTab',
      module: 'coordinator',
      coordinatorTabSlug: 'embed',
      isPanelTarget: true,
      advanceGate: { type: 'manual' },
    },
    {
      id: 'embed_code_tab',
      stepName: 'lab_embed_code_tab',
      kind: 'navigate',
      groupId: 'tabs',
      tourAnchor: 'embed-tab-code',
      title: 'Embed code',
      instruction: 'Switch to the Code tab to copy the HTML snippet.',
      metadata: { embedTab: 'code' },
      beforeAction: 'setEmbedTab',
      isPanelTarget: true,
      targetZone: 'embed-tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'embed-tab-code' },
    },
    {
      id: 'embed_code',
      stepName: 'lab_embed_code',
      kind: 'spotlight',
      groupId: 'tabs',
      tourAnchor: 'embed-tab-code',
      title: 'Copy snippet',
      instruction: 'Copy the embed code and paste it into your agency site CMS.',
      metadata: { embedTab: 'code' },
      beforeAction: 'setEmbedTab',
      module: 'coordinator',
      coordinatorTabSlug: 'embed',
      isPanelTarget: true,
      advanceGate: { type: 'manual' },
    },
    {
      id: 'embed_preview_tab',
      stepName: 'lab_embed_preview_tab',
      kind: 'navigate',
      groupId: 'tabs',
      tourAnchor: 'embed-tab-preview',
      title: 'Preview tab',
      instruction: 'Open Preview to see the widget before going live.',
      metadata: { embedTab: 'preview' },
      beforeAction: 'setEmbedTab',
      isPanelTarget: true,
      targetZone: 'embed-tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'embed-tab-preview' },
    },
    {
      id: 'embed_preview',
      stepName: 'lab_embed_preview',
      kind: 'spotlight',
      groupId: 'tabs',
      tourAnchor: 'embed-tab-preview',
      title: 'Preview widget',
      instruction: 'Confirm the calendar looks correct for your agency branding.',
      metadata: { embedTab: 'preview' },
      beforeAction: 'setEmbedTab',
      module: 'coordinator',
      coordinatorTabSlug: 'embed',
      isPanelTarget: true,
      advanceGate: { type: 'manual' },
    },
    {
      id: 'embed_complete',
      stepName: 'lab_embed_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Embed flow complete',
      instruction: 'You can return here anytime to update embed settings.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};

const agencyProfilePlaybook: LearningLabPlaybook = {
  slug: 'coordinator_agency_profile',
  title: 'Agency profile',
  groups: [
    { id: 'start', title: 'Settings' },
    { id: 'finish', title: 'Wrap up' },
  ],
  steps: [
    {
      id: 'profile_intro',
      stepName: 'lab_profile_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Agency profile',
      instruction: 'Public agency details appear on your APT profile and help officers find you.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'profile_settings_tab',
      stepName: 'lab_profile_settings_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-settings',
      title: 'Open Settings',
      instruction: 'Open Agency Settings on the coordinator dashboard.',
      module: 'coordinator',
      coordinatorTabSlug: 'settings',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-settings' },
    },
    {
      id: 'profile_description',
      stepName: 'lab_profile_description',
      kind: 'form',
      groupId: 'start',
      tourAnchor: 'coordinator-agency-description',
      title: 'Agency description',
      instruction: 'Review the description field — this text appears on your public agency page.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'settings',
      beforeAction: 'switchCoordinatorTab',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'profile_edit',
      stepName: 'lab_profile_edit',
      kind: 'interact',
      groupId: 'start',
      tourAnchor: 'coordinator-agency-edit-profile-btn',
      title: 'Open edit profile',
      instruction: 'Click Edit Agency Profile to open the editor dialog.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'settings',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'open-modal',
      advanceGate: { type: 'click', anchor: 'coordinator-agency-edit-profile-btn' },
    },
    {
      id: 'profile_save',
      stepName: 'lab_profile_save',
      kind: 'confirm',
      groupId: 'start',
      tourAnchor: 'coordinator-agency-save-btn',
      title: 'Save (practice)',
      instruction: 'Saving is blocked in the Lab — you will see a practice toast if you try.',
      isPanelTarget: true,
      optional: true,
      module: 'coordinator',
      coordinatorTabSlug: 'settings',
      beforeAction: 'switchCoordinatorTab',
      metadata: { requiredModal: 'agency-edit' },
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-agency-save-btn' },
    },
    {
      id: 'profile_complete',
      stepName: 'lab_profile_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Profile tour complete',
      instruction: 'Keep your agency description current so search and SEO stay accurate.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};

const certificatesPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_certificates',
  title: 'Certificates',
  groups: [
    { id: 'start', title: 'Certificates' },
    { id: 'finish', title: 'Wrap up' },
  ],
  steps: [
    {
      id: 'cert_intro',
      stepName: 'lab_cert_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Agency certificates',
      instruction: 'Manage certificate templates and view issued certificates for your agency.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'cert_tab',
      stepName: 'lab_cert_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-certificates',
      title: 'Open Certificates',
      instruction: 'Open the Certificates tab.',
      module: 'coordinator',
      coordinatorTabSlug: 'certificates',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-certificates' },
    },
    {
      id: 'cert_templates',
      stepName: 'lab_cert_templates',
      kind: 'spotlight',
      groupId: 'start',
      tourAnchor: 'coordinator-cert-templates',
      title: 'Templates',
      instruction: 'Templates define layout and fields for certificates your agency issues.',
      metadata: { certificateTab: 'templates' },
      beforeAction: 'setCertificateTab',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'certificates',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'cert_issued_tab',
      stepName: 'lab_cert_issued_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-cert-tab-issued',
      title: 'Issued certificates',
      instruction: 'Switch to the issued list to see sample certificates from completed events.',
      metadata: { certificateTab: 'issued' },
      beforeAction: 'setCertificateTab',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'certificates',
      targetZone: 'embed-tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-cert-tab-issued' },
    },
    {
      id: 'cert_issued',
      stepName: 'lab_cert_issued',
      kind: 'spotlight',
      groupId: 'start',
      tourAnchor: 'coordinator-cert-issued-list',
      title: 'Sample issued row',
      instruction: 'Review how issued certificates appear for officers who completed training.',
      metadata: { certificateTab: 'issued' },
      beforeAction: 'setCertificateTab',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'certificates',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'cert_complete',
      stepName: 'lab_cert_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Certificates tour complete',
      instruction: 'Use templates and issued lists to track compliance after events.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};


const outsideTrainingPlaybook: LearningLabPlaybook = {
  slug: 'coordinator_outside_training',
  title: 'Review outside training',
  groups: [
    { id: 'start', title: 'Outside training' },
    { id: 'review', title: 'Review' },
    { id: 'finish', title: 'Wrap up' },
  ],
  steps: [
    {
      id: 'outside_intro',
      stepName: 'lab_outside_intro',
      kind: 'intro',
      groupId: 'start',
      title: 'Review outside training',
      instruction:
        'Practice approving a sample outside-training submission. Saves are blocked in the Lab.',
      why: 'Members log training completed elsewhere; coordinators review for agency credit.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
    {
      id: 'outside_tab',
      stepName: 'lab_outside_tab',
      kind: 'navigate',
      groupId: 'start',
      tourAnchor: 'coordinator-tab-outside-training',
      title: 'Open Outside Training',
      instruction: 'Open the Outside Training tab on the coordinator dashboard.',
      module: 'coordinator',
      coordinatorTabSlug: 'outside-training',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'tab',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: { type: 'click', anchor: 'coordinator-tab-outside-training' },
    },
    {
      id: 'outside_row',
      stepName: 'lab_outside_row',
      kind: 'spotlight',
      groupId: 'review',
      tourAnchor: 'coordinator-outside-training-row-pending',
      title: 'Select the pending record',
      instruction:
        'Find the Defensive Tactics Refresher row — it is seeded pending for practice.',
      why: 'You review one submission at a time from the list.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'outside-training',
      beforeAction: 'switchCoordinatorTab',
      advanceGate: { type: 'manual' },
    },
    {
      id: 'outside_review_btn',
      stepName: 'lab_outside_review_btn',
      kind: 'interact',
      groupId: 'review',
      tourAnchor: 'coordinator-outside-training-review-btn',
      title: 'Open Review',
      instruction: 'Click Review to open the approval dialog.',
      isPanelTarget: true,
      module: 'coordinator',
      coordinatorTabSlug: 'outside-training',
      beforeAction: 'switchCoordinatorTab',
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      metadata: { rowAnchor: 'coordinator-outside-training-row-pending' },
      advanceGate: { type: 'click', anchor: 'coordinator-outside-training-review-btn' },
    },
    {
      id: 'outside_modal',
      stepName: 'lab_outside_modal',
      kind: 'form',
      groupId: 'review',
      tourAnchor: 'coordinator-outside-training-review-modal',
      title: 'Review the submission',
      instruction:
        'Confirm the training details. In the Lab, Approve is pre-selected so you can practice submit.',
      isPanelTarget: true,
      advanceGate: {
        type: 'dom',
        event: 'apt-tour:modal-opened',
        detail: { modalId: 'outside-training-review' },
      },
    },
    {
      id: 'outside_submit',
      stepName: 'lab_outside_submit',
      kind: 'confirm',
      groupId: 'finish',
      tourAnchor: 'coordinator-outside-training-review-submit-btn',
      title: 'Submit review (practice)',
      instruction:
        'Click Submit Review — the Lab intercepts the request and shows a practice message.',
      tip: 'On your real agency, this updates the member training record.',
      isPanelTarget: true,
      metadata: { requiredModal: 'outside-training-review' },
      targetZone: 'header-action',
      scrollPriority: 'safe-viewport',
      panelFallback: 'dispatch',
      advanceGate: {
        type: 'click',
        anchor: 'coordinator-outside-training-review-submit-btn',
      },
    },
    {
      id: 'outside_complete',
      stepName: 'lab_outside_complete',
      kind: 'complete',
      groupId: 'finish',
      title: 'Outside training review complete',
      instruction: 'You practiced reviewing outside training. No live records were changed.',
      advanceGate: { type: 'manual' },
      placement: 'center',
    },
  ],
};

export const LEARNING_LAB_PLAYBOOKS: Record<
  CoordinatorLabTourSlug,
  LearningLabPlaybook
> = {
  coordinator_cancel_event: cancelPlaybook,
  coordinator_create_event: createEventPlaybook,
  coordinator_create_user: createUserPlaybook,
  coordinator_manage_venues: venuesPlaybook,
  coordinator_embed_calendar: embedPlaybook,
  coordinator_agency_profile: agencyProfilePlaybook,
  coordinator_certificates: certificatesPlaybook,
  coordinator_outside_training: outsideTrainingPlaybook,
};

export function getLearningLabPlaybook(
  slug: string,
): LearningLabPlaybook | undefined {
  return LEARNING_LAB_PLAYBOOKS[slug as CoordinatorLabTourSlug];
}

export function getMissionStepCount(slug: CoordinatorLabTourSlug): number {
  return LEARNING_LAB_PLAYBOOKS[slug].steps.length;
}
